import type { FC, PropsWithChildren } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Button, Flex, Icon, Typography } from '@ui';
import clsx from 'clsx';
import styles from './Modal.module.css';

type Props = PropsWithChildren<{
    open?: boolean;
    onClose?: () => void;
    showCloseButton?: boolean;
    closeOnOverlayClick?: boolean;
    ['aria-title']?: string;
    ['aria-description']?: string;
}>;

type Subcomponents = {
    Title: FC<PropsWithChildren>;
    Description: FC<PropsWithChildren>;
};

const Modal: FC<Props> & Subcomponents = ({ open, onClose, showCloseButton = true, closeOnOverlayClick = true, children, ...props }) => (
    <RadixDialog.Root open={open}>
        <RadixDialog.Overlay
            onClick={(closeOnOverlayClick && onClose) ? onClose : undefined}
            className={styles.overlay}
        />
        <Flex
            data-open={open}
            justify="center"
            align="center"
            onClick={(closeOnOverlayClick && onClose) ? onClose : undefined}
            className={styles.modal}
        >
            <RadixDialog.Content
                onClick={e => e.stopPropagation()}
                className={clsx(styles.content, !open && styles.contentClose)}
            >
                {showCloseButton && (
                    <RadixDialog.Close className={styles.close}>
                        <Button icon={<Icon.Close size={20} />} onClick={onClose} />
                    </RadixDialog.Close>
                )}
                <VisuallyHidden.Root>
                    <>
                        <RadixDialog.Title>{props['aria-title']}</RadixDialog.Title>
                        <RadixDialog.Description>{props['aria-description']}</RadixDialog.Description>
                    </>
                </VisuallyHidden.Root>
                {children}
            </RadixDialog.Content>
        </Flex>
    </RadixDialog.Root>
);

Modal.Title = ({ children }) => (
    <RadixDialog.Title asChild>
        <Typography.Heading level={4}>{children}</Typography.Heading>
    </RadixDialog.Title>
);

Modal.Description = ({ children }) => (
    <RadixDialog.Description asChild>
        <Typography.Paragraph level={2}>{children}</Typography.Paragraph>
    </RadixDialog.Description>
);

export { Modal };
